import {TFunction} from 'i18next';
import {getComment, I_Comment} from '../../comment/state/apiGet';
import {IComment} from '../../comment/type';
import {IS_CLIENT} from '../../common/constant';
import {
  getColor,
  getContentProps,
  getImageUrl,
  getPlatformSource,
  getVideoSources,
  I_ContentMeta,
  I_ExternalVideo,
} from '../../common/state/apiGet';
import {IExternalVideoType} from '../../common/type';
import {
  formatFromServerDate,
  getNowFromServer,
  getRemainText,
  getTimeStamp,
} from '../../common/util/date';
import {round} from '../../common/util/number';
import {parseLandingUriFromServer} from '../../common/util/PageRouter';
import {convertHexToRgb, toCamelCase} from '../../common/util/string';
import {IRewardType} from '../../commonSub/type';
import {
  IEventBottomModule,
  IEventButtonModule,
  IEventRecommendModule,
  IEventRewardModule,
  IEventTopModule,
  IEventVideoModule,
  IEventImageModule,
  IEventCommentModule,
  IEventMissionModule,
  IMissionAttendance,
  IMissionPreAttendance,
  IMissionContentReservation,
  IMissionAttendanceType,
  IMissionAttendanceRewardType,
  IMissionQuiz,
  IEventShareModule,
  IEventModuleMap,
  IEventUnionModule,
  IModuleMapV4,
  ITopModuleV4,
  IRewardModuleV4,
  IVideoModuleV4,
  IModuleSectionV4,
  IAttendanceModuleV4,
  IAttendanceRewardTypeV4,
  IPreRequestModuleV4,
  IContentBookingModuleV4,
  IQuizModuleV4,
  IUnionModuleV4,
  IImageModuleV4,
  IButtonModuleV4,
  IShareModuleV4,
  ICommentModuleV4,
  IRecommendModuleV4,
  IEventAnchorCardV4,
} from '../type';

interface I_TopModule {
  id: number;
  expiresDateTime: string;
  title: string;
  backgroundColor: string;
  backgroundImage?: string;
  eventMainImage: string;
  eventMainVideo?: string;
  copyright?: string;
}

interface I_BottomModule {
  id: number;
  availableFromDateTime: string;
  availableToDateTime: string;
  rewardNotice?: string;
  detailNotice: string;
}

interface I_RewardModule {
  id: number;
  missionProgress?: I_RewardProgressBar;
  rewards: Array<{
    missionTitle: string;
    rewardTitle: string;
    missionProgress?: I_RewardProgressBar;
    reward: {
      type: string;
      circleImage: boolean;
      iconImage: string;
    };
  }>;
}

interface I_RewardProgressBar {
  goal: number;
  done: number;
  completed: boolean;
}

interface I_MissionModule {
  id: number;
  // 출석체크 리워드
  attendance?: I_MissionAttendance;
  // 출석체크 사전신청
  participantsAcquire?: I_MissionPreAttendance;
  // 작품 사전예약
  contentReservation?: I_MissionContentReservation;
  // 퀴즈
  quiz?: I_MissionQuiz;
}
interface I_MissionAttendance {
  title: string;
  description: string;
  missions: Array<{
    id: number;
    attendanceRule: 'READING' | 'CLICK';
    completed: boolean;
    now: boolean;
    round: number; // "회차(round) 회차 출석하기"
    reward?: {
      type: string;
      quantity: number; // 캐시
      missionImage: string;
    };
  }>;
}
interface I_MissionPreAttendance {
  title: string;
  description: string;
  completed: boolean;
}
interface I_MissionContentReservation {
  title: string;
  description: string;
  completed: boolean;
  content: I_ContentMeta;
}

interface I_MissionQuiz {
  missionId: number;
  completed: boolean;
  title: string;
  quiz: string;
  answer: string;
  hint?: string;
  buttonLanding?: {
    buttonLandingUrl: string;
    buttonLandingTitle: string;
    content: I_ContentMeta;
  };
  reward: {
    missionImage: string;
    quantity: number;
    type: 'CASH' | 'LUCKY_DRAW' | 'TICKET' | 'RAFFLE';
  };
}

interface I_ImageModule {
  id: number;
  title: string;
  description: string;
  operationImage: string;
}

interface I_CommentModule {
  id: number;
  eventId: number;
}

interface I_RecommendContent extends I_ContentMeta {
  beforeSelling: boolean;
}
interface I_RecommendModule {
  id: number;
  title: string;
  contents: Array<{
    content: I_RecommendContent;
    subscript: boolean;
  }>;
}

interface I_VideoModule {
  id: number;
  title: string;
  description: string;
  externalVideoKey: string;
  externalVideoFrom: I_ExternalVideo;
  isStreaming: boolean;
}

interface I_ButtonModule {
  id: number;
  title: string;
  description: string;
  buttonTitle: string;
  landingUrl: string;
}

interface I_ShareModule {
  id: number;
}
interface I_UnionImageModule extends I_ImageModule {
  type: 'image';
}
interface I_UnionButtonModule extends I_ButtonModule {
  type: 'button';
}
interface I_UnionVideoModule extends I_VideoModule {
  type: 'video';
}

interface I_UnionModule {
  id: number;
  title: string;
  description: string;
  modules: Array<I_UnionImageModule | I_UnionButtonModule | I_UnionVideoModule>;
}

interface I_EventModules {
  questCampaignId: number;
  top: I_TopModule;
  bottom: I_BottomModule;
  rewardModule?: I_RewardModule;
  missionModule?: I_MissionModule;
  imageModule?: I_ImageModule;
  commentModule?: I_CommentModule;
  contentRecommendationModule?: I_RecommendModule;
  videoModule?: I_VideoModule;
  buttonModule?: I_ButtonModule;
  shareModule?: I_ShareModule;
  unionModule?: I_UnionModule;
  responseDateTime?: string;
}

function getTopModule({
  data,
  t,
  now,
}: {
  data: I_TopModule;
  t: TFunction;
  now: number;
}): IEventTopModule {
  const bgColor = getColor(data.backgroundColor);
  return {
    id: data.id,
    back: {
      bgColor,
      bgColorRgb: convertHexToRgb(bgColor),
      bgImg: data.backgroundImage
        ? getImageUrl(data.backgroundImage)
        : undefined,
      // 비디오 poster 속성 사용을 위해 png 를 직접 사용한다.
      eventMainImg: data.eventMainVideo
        ? `${getPlatformSource(data.eventMainImage)}.png`
        : getImageUrl(data.eventMainImage, {hasAlpha: true}),
      eventMainVideoSources: data.eventMainVideo
        ? getVideoSources(data.eventMainVideo)
        : undefined,
    },
    front: {
      remainText: getRemainText(
        {
          endDate: data.expiresDateTime,
          now,
          isShortUnit: true,
        },
        t
      ),
      title: data.title.replace(/\n/g, ' '),
      titles: data.title.split('\n'),
      copyright: data.copyright,
    },
  };
}

function getBottomModule(data: I_BottomModule): IEventBottomModule {
  return {
    startDate: formatFromServerDate(data.availableFromDateTime),
    endDate: formatFromServerDate(data.availableToDateTime),
    rewardDescription: data.rewardNotice,
    detailDescriptions: data.detailNotice.split('\n'),
  };
}

function getRewardModule({
  id,
  missionProgress,
  rewards = [],
}: I_RewardModule): IEventRewardModule {
  return {
    id,
    topProgressBar: getRewardProgressBar(missionProgress),
    rewards: rewards.map(item => ({
      missionTitle: item.missionTitle,
      rewardTitle: item.rewardTitle,
      progressBar: getRewardProgressBar(item.missionProgress),
      reward: {
        type: item.reward.type,
        isCircleIcon: item.reward.circleImage,
        icon: getImageUrl(item.reward.iconImage, {hasAlpha: true}),
      },
    })),
  };
}

function getRewardProgressBar(data?: I_RewardProgressBar) {
  return data && data.done >= 0
    ? {
        ratioText: `${data.done} / ${data.goal}`,
        percent: round((data.done / data.goal) * 100),
        isComplete: data.completed,
        currentValue: data.done,
        maxValue: data.goal,
      }
    : undefined;
}

function getMissonModule(data: I_MissionModule): IEventMissionModule {
  return {
    id: data.id,
    attendance: data.attendance && getMissionAttendance(data.attendance),
    preAttendance:
      data.participantsAcquire &&
      getMissionPreAttendance(data.participantsAcquire),
    contentReservation:
      data.contentReservation &&
      getMissionContentReservation(data.contentReservation),
    quiz: data.quiz && getMissionQuiz(data.quiz),
  };
}
function getMissionAttendance({
  title,
  description,
  missions = [],
}: I_MissionAttendance): IMissionAttendance {
  return {
    title,
    description,
    missions: missions.map(item => ({
      id: item.id,
      type: toCamelCase(item.attendanceRule) as IMissionAttendanceType,
      isComplete: item.completed,
      isActive: item.now,
      episodeRound: item.round,
      reward: item.reward
        ? {
            type: toCamelCase(item.reward.type) as IMissionAttendanceRewardType,
            quantity: item.reward.quantity,
            image: getImageUrl(item.reward.missionImage, {hasAlpha: true}),
          }
        : undefined,
    })),
  };
}
function getMissionPreAttendance({
  title,
  description,
  completed: isComplete,
}: I_MissionPreAttendance): IMissionPreAttendance {
  return {title, description, isComplete};
}
function getMissionContentReservation({
  title,
  description,
  completed: isComplete,
  content,
}: I_MissionContentReservation): IMissionContentReservation {
  return {
    title,
    description,
    isComplete,
    content: getContentProps(
      content,
      ['id', 'sid', 'title', 'descLine2', 'mainImg', 'bgImg'],
      {isBigBg: true}
    ),
  };
}
function getMissionQuiz({
  missionId: id,
  completed: isComplete,
  title,
  quiz,
  answer,
  hint,
  buttonLanding,
  reward,
}: I_MissionQuiz): IMissionQuiz {
  return {
    id,
    isComplete,
    moduleTitle: title,
    title: quiz,
    answer,
    hint,
    buttonLanding: buttonLanding
      ? {
          url: parseLandingUriFromServer(buttonLanding.buttonLandingUrl),
          content: getContentProps(buttonLanding.content, [
            'id',
            'sid',
            'title',
            'mainImg',
            'bgImg',
          ]),
        }
      : undefined,
    reward: {
      type: toCamelCase(reward.type) as IRewardType,
      quantity: reward.quantity,
    },
  };
}

function getImageModule({
  id,
  title,
  description,
  operationImage,
}: I_ImageModule): IEventImageModule {
  return {
    id,
    title,
    description,
    image: getImageUrl(operationImage, {hasAlpha: true}),
  };
}

function getCommentModule({id, eventId}: I_CommentModule): IEventCommentModule {
  return {id, eventId, comments: []};
}

function getRecommendModule({
  id,
  title,
  contents = [],
}: I_RecommendModule): IEventRecommendModule {
  return {
    id,
    title,
    contents: contents.map(item => {
      const {seoKeywords, ...contentProps} = getContentProps(
        item.content,
        [
          'id',
          'sid',
          'title',
          'mainImg',
          'bgImg',
          'seoKeywords',
          'descLine2',
          'badges',
        ],
        {isBigBg: true}
      );
      return {
        content: {
          seoKeywords: seoKeywords.slice(0, 2),
          isBlockRouting: item.content.beforeSelling,
          ...contentProps,
        },
        isSubscribe: item.subscript,
      };
    }),
  };
}

function getVideoModule(data: I_VideoModule): IEventVideoModule {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    key: data.externalVideoKey,
    type: toCamelCase(data.externalVideoFrom) as IExternalVideoType,
    isStreaming: !!data.isStreaming,
  };
}

function getButtonModule(data: I_ButtonModule): IEventButtonModule {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    buttonTitle: data.buttonTitle,
    landingUri: parseLandingUriFromServer(data.landingUrl),
  };
}

function getShareModule(data: I_ShareModule): IEventShareModule {
  return {id: data.id};
}

function getUnionModule(data: I_UnionModule): IEventUnionModule {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    modules: data.modules.map(item => {
      switch (item.type) {
        case 'image':
          return {moduleType: 'image', module: getImageModule(item)};
        case 'button':
          return {moduleType: 'button', module: getButtonModule(item)};
        case 'video':
          return {moduleType: 'video', module: getVideoModule(item)};
      }
    }),
  };
}

export function getEventModuleMap({
  data: {
    questCampaignId,
    top,
    bottom,
    rewardModule,
    missionModule,
    imageModule,
    commentModule,
    contentRecommendationModule,
    buttonModule,
    videoModule,
    shareModule,
    unionModule,
    responseDateTime,
  },
  eventId,
  t,
}: {
  data: I_EventModules;
  eventId: number;
  t: TFunction;
}): {
  data: IEventModuleMap;
  isExpired: boolean;
  showLoading: boolean;
  eventId: number;
  questCampaignId: number;
} {
  const now = getNowFromServer(responseDateTime);
  const isExpired = now > getTimeStamp(top.expiresDateTime);
  return {
    data: {
      topModule: getTopModule({data: top, t, now}),
      bottomModule: getBottomModule(bottom),
      rewardModule: rewardModule ? getRewardModule(rewardModule) : undefined,
      missionModule: missionModule ? getMissonModule(missionModule) : undefined,
      imageModule: imageModule?.operationImage
        ? getImageModule(imageModule)
        : undefined,
      commentModule: commentModule
        ? getCommentModule(commentModule)
        : undefined,
      recommendModule: contentRecommendationModule
        ? getRecommendModule(contentRecommendationModule)
        : undefined,
      videoModule: videoModule ? getVideoModule(videoModule) : undefined,
      buttonModule: buttonModule ? getButtonModule(buttonModule) : undefined,
      shareModule: shareModule ? getShareModule(shareModule) : undefined,
      unionModule: unionModule?.modules.length
        ? getUnionModule(unionModule)
        : undefined,
    },
    isExpired,
    showLoading: !IS_CLIENT,
    eventId,
    questCampaignId,
  };
}

export function getEventComments(
  data: {commentList: I_Comment[]},
  t: TFunction
): IComment[] {
  const now = Date.now();
  return data.commentList.map(comment => getComment({comment, now, t}));
}

export function getIsExpiredEvent({available}: {available: boolean}) {
  return available === false;
}

// TODO: 아래부터는 바뀐 타입에 맞춰 컨버팅하기 위해 추가된 함수들. 나중에 같은 이름의 V4 가 없는 함수를 제거하자.

function getTopModuleV4(data: I_TopModule, t: TFunction): ITopModuleV4 {
  const bgColor = getColor(data.backgroundColor);
  return {
    bgColor,
    bgColorRgb: convertHexToRgb(bgColor),
    bgImg: data.backgroundImage ? getImageUrl(data.backgroundImage) : undefined,
    thumbImg: data.eventMainVideo
      ? `${getPlatformSource(data.eventMainImage)}.png`
      : getImageUrl(data.eventMainImage, {hasAlpha: true}),
    videoSources: data.eventMainVideo
      ? getVideoSources(data.eventMainVideo)
      : undefined,
    remainText: getRemainText(
      {
        endDate: data.expiresDateTime,
        now: Date.now(),
        isShortUnit: true,
      },
      t
    ),
    titles: data.title.split('\n'),
    copyright: data.copyright,
  };
}

function getRewardModuleV4(data: I_RewardModule): IRewardModuleV4 {
  return {
    // TODO: 다음에 추가될 스펙이다. API 변경되면 수정하자.
    isEmphasis: false,
    progressBar: getRewardProgressBar(data.missionProgress),
    rewardList: (data.rewards ?? []).map(item => {
      const progress = getRewardProgressBar(item.missionProgress);
      return {
        // TODO: isEmphasis 값 여부에 따라 존재할 데이터.
        label: '',
        title: item.missionTitle,
        subTitle: item.rewardTitle,
        isCompleted: !!progress?.isComplete,
        isBgPurple: item.reward.circleImage,
        reward: {
          type: toCamelCase(item.reward.type) as IRewardType,
          quantity: 0,
          image: getImageUrl(item.reward.iconImage, {hasAlpha: true}),
        },
      };
    }),
  };
}

function getSection({
  title,
  description,
}: {
  title?: string;
  description?: string;
}): IModuleSectionV4 {
  return {
    section: {
      title,
      description,
    },
  };
}

function getVideoModuleV4(data: I_VideoModule): IVideoModuleV4 {
  return {
    moduleType: 'video',
    ...getSection(data),
    videoInfo: {
      key: data.externalVideoKey,
      type: toCamelCase(data.externalVideoFrom) as IExternalVideoType,
      isStreaming: !!data.isStreaming,
    },
  };
}

function getMissionAttendanceModuleV4(
  data: I_MissionAttendance
): IAttendanceModuleV4 {
  const currentRound =
    data.missions.find(item => item.now)?.round || data.missions.length;

  return {
    moduleType: 'missionAttendance',
    ...getSection(data),
    attendanceList: (data.missions ?? []).map(item => ({
      type: toCamelCase(item.attendanceRule) as IMissionAttendanceType,
      isCurrent: item.now,
      isCompleted: item.completed,
      attendanceCount: item.round,
      round: item.round,
      currentRound,
      reward: item.reward
        ? {
            type: toCamelCase(item.reward.type) as IAttendanceRewardTypeV4,
            quantity: item.reward.quantity,
            image: getImageUrl(item.reward.missionImage, {hasAlpha: true}),
          }
        : undefined,
    })),
  };
}

function getMissionPreRequestModuleV4(
  data: I_MissionPreAttendance
): IPreRequestModuleV4 {
  return {
    moduleType: 'missionPreRequest',
    ...getSection(data),
    isCompleted: data.completed,
  };
}

function getAnchorContentProps(
  content: I_ContentMeta,
  isBlockRouting = false
): IEventAnchorCardV4 {
  const contentProps = getContentProps(
    content,
    [
      'id',
      'sid',
      'badges',
      'bgImg',
      'title',
      'mainImg',
      'mainTitleImg',
      'descLine2',
      'seoKeywords',
      'authors',
    ],
    {isBigBg: true}
  );
  return {
    ...contentProps,
    seoKeywords: contentProps.seoKeywords
      ? contentProps.seoKeywords.slice(0, 2)
      : undefined,
    isBlockRouting,
  };
}

function getMissionContentBookingModuleV4(
  data: I_MissionContentReservation
): IContentBookingModuleV4 {
  return {
    moduleType: 'missionContentBooking',
    ...getSection(data),
    isCompleted: data.completed,
    contentAnchor: getAnchorContentProps(data.content),
  };
}

function getMissionQuizMOduleV4(data: I_MissionQuiz): IQuizModuleV4 {
  return {
    moduleType: 'missionQuiz',
    ...getSection(data),
    isCompleted: data.completed,
    quizTitle: data.quiz,
    hint: data.hint,
    answer: data.answer,
    reward: {
      type: toCamelCase(data.reward.type) as IRewardType,
      quantity: data.reward.quantity,
    },
    landingInfo: data.buttonLanding
      ? {
          landingUrl: parseLandingUriFromServer(
            data.buttonLanding.buttonLandingUrl
          ),
          contentAnchor: getAnchorContentProps(data.buttonLanding.content),
        }
      : undefined,
  };
}

function getUnionModuleV4(data: I_UnionModule): IUnionModuleV4 {
  return {
    moduleType: 'union',
    ...getSection(data),
    moduleList: data.modules
      .map(item => {
        switch (item.type) {
          case 'image':
            return getImageModuleV4(item);
          case 'button':
            return getButtonModuleV4(item);
          case 'video':
            return getVideoModuleV4(item);
        }
      })
      .filter(Boolean),
  };
}

function getImageModuleV4(data: I_ImageModule): IImageModuleV4 {
  return {
    moduleType: 'image',
    ...getSection(data),
    // TODO: 다음 스프린트에 이미지는 리스트로 내려올 예정.
    imageList: [getImageUrl(data.operationImage, {hasAlpha: true})],
  };
}

function getButtonModuleV4(data: I_ButtonModule): IButtonModuleV4 {
  return {
    moduleType: 'button',
    ...getSection(data),
    buttonLabel: data.buttonTitle,
    landingUrl: parseLandingUriFromServer(data.landingUrl),
  };
}

function getShareModuleV4(_: I_ShareModule): IShareModuleV4 {
  return {moduleType: 'share'};
}

function getCommentModuleV4(_: I_CommentModule): ICommentModuleV4 {
  return {
    moduleType: 'comment',
    commentList: [],
  };
}

function getRecommendModuleV4(data: I_RecommendModule): IRecommendModuleV4 {
  return {
    moduleType: 'recommend',
    ...getSection(data),
    // TODO: 다음 스프린트에 리스트 뷰 타입에 따라 변경되어야 한다.
    divisionCount: 1,
    recommendAnchorList: (data.contents ?? []).map(item => {
      return {
        ...getAnchorContentProps(item.content, item.content.beforeSelling),
        isLike: item.subscript,
      };
    }),
  };
}

export function getEventModuleMapV4({
  data,
  eventId,
  t,
}: {
  data: I_EventModules;
  eventId: number;
  t: TFunction;
}): IModuleMapV4 {
  const {top, rewardModule, missionModule, bottom, questCampaignId} = data;
  const isExpired = Date.now() > getTimeStamp(top.expiresDateTime);
  const moduleList = [];

  // TODO: 다음 스프린트에선 모듈 리스트가 새로 내려올 예정이다. 아래 if 조건문은 전부 지우면 된다.
  if (data.videoModule) {
    moduleList.push(getVideoModuleV4(data.videoModule));
  }
  if (missionModule?.attendance) {
    moduleList.push(getMissionAttendanceModuleV4(missionModule.attendance));
  }
  if (missionModule?.participantsAcquire) {
    moduleList.push(
      getMissionPreRequestModuleV4(missionModule.participantsAcquire)
    );
  }
  if (missionModule?.contentReservation) {
    moduleList.push(
      getMissionContentBookingModuleV4(missionModule.contentReservation)
    );
  }
  if (missionModule?.quiz) {
    moduleList.push(getMissionQuizMOduleV4(missionModule.quiz));
  }
  if (data.unionModule && data.unionModule.modules.length > 0) {
    moduleList.push(getUnionModuleV4(data.unionModule));
  }
  if (data.imageModule) {
    moduleList.push(getImageModuleV4(data.imageModule));
  }
  if (data.buttonModule) {
    moduleList.push(getButtonModuleV4(data.buttonModule));
  }
  if (data.shareModule) {
    moduleList.push(getShareModuleV4(data.shareModule));
  }
  if (data.commentModule) {
    moduleList.push(getCommentModuleV4(data.commentModule));
  }
  if (data.contentRecommendationModule) {
    moduleList.push(getRecommendModuleV4(data.contentRecommendationModule));
  }

  return {
    commonModule: {
      eventTitle: top.title?.replace(/\n/g, ' '),
      questCampaignId,
      eventId,
      isExpired,
      bgColor: getColor(top.backgroundColor),
    },
    topModule: getTopModuleV4(top, t),
    rewardModule: rewardModule ? getRewardModuleV4(rewardModule) : undefined,
    moduleList: moduleList.length > 0 ? moduleList : undefined,
    bottomModule: bottom ? getBottomModule(bottom) : undefined,
  };
}
